import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3TherapyPageState from './useTreatmentPlanV3TherapyPageState';

import type { AppDispatch } from '../../../../../../store';
import type { TherapyPayload } from '../../../../../common';
import type {
  TreatmentPlanV3TherapiesDependencies,
  UseTreatmentPlanV3TherapiesResult,
} from './types';

export default function useTreatmentPlanV3Therapies() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const pageState = useTreatmentPlanV3TherapyPageState();

  return useTreatmentPlanV3TherapiesWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    ...pageState,
  });
}

export function useTreatmentPlanV3TherapiesWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  therapies,
  noTherapy,
  isLoading,
  hasLoaded,
  hasLoadError,
  isSaving,
  isBulkSaving,
}: TreatmentPlanV3TherapiesDependencies): UseTreatmentPlanV3TherapiesResult {
  const create = useCallback(
    async (data: TherapyPayload) => {
      if (!treatmentPlanId) return undefined;
      const result = await dispatch(
        actions.createTreatmentPlanV3Therapy({ treatmentPlanId, data, getAccessTokenSilently }),
      );
      return actions.createTreatmentPlanV3Therapy.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const update = useCallback(
    async (therapyId: number, data: TherapyPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3Therapy({
          treatmentPlanId,
          therapyId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3Therapy.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (therapyId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3Therapy({
          treatmentPlanId,
          therapyId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3Therapy.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const setNoTherapies = useCallback(
    async (nextNoTherapy: boolean) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.setTreatmentPlanV3NoTherapy({
          treatmentPlanId,
          noTherapy: nextNoTherapy,
          getAccessTokenSilently,
        }),
      );
      return actions.setTreatmentPlanV3NoTherapy.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    therapies,
    noTherapy,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    setNoTherapies,
  };
}
